import { useState, useContext } from 'react';
import {
  Button,
  ButtonStyles,
  FAIcon,
  useNextgenRouter,
  useTranslation,
  TextLink,
  TextLinkStyle,
  useSession,
  GenericLink,
} from '@nextgen-web-framework/all';
import { SocialShareLink } from '@commerce-webcomponents/ui';
import {
  CartRoutePaths,
  ListEntry,
  ListResponse,
  ListType,
  capitalizeFirstLetter,
  myListApi,
  getCategoryNameByCategoryId,
  listCategoryDataTransformer,
  getCategoryColor,
  categoryDataType,
  getButtonThemeColor,
} from '@commerce-cart-webcomponents/sdk';
import {
  DisplayListType,
  AddProductsDrawer,
  ListMenu,
  AllCustomListContext,
  ModalWrapper,
  MiniCart,
  useFetchCmsContent,
} from '@commerce-cart-webcomponents/ui';
import type { CustomListContextType } from '@commerce-cart-webcomponents/ui';
import styles from './ListBanner.module.scss';

export type ListBannerProps = {
  listDetails?: ListResponse;
  showLoaderCB?: (show: boolean) => void;
};

export const ListBanner = ({ listDetails, showLoaderCB }: ListBannerProps) => {
  const router = useNextgenRouter();
  const t = useTranslation();
  const { isABO } = useSession();
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);
  const [invalidProducts, setInvalidProducts] = useState<ListEntry[]>([]);

  const primaryLabelText = t('cart.invalidProduct.cancel');
  const secondaryLabelText = t('cart.invalidProduct.removeItem');
  const headLineText = t('cart.invalidProductModal.heading');
  const description = t('cart.invalidProductModal.description');

  const categoryData = listCategoryDataTransformer(useFetchCmsContent());

  const categoryName = getCategoryNameByCategoryId(listDetails?.payLoad?.category || '', categoryData);

  const onChevronLeftClick = (
    e:
      | React.MouseEvent<HTMLDivElement | HTMLAnchorElement | HTMLSpanElement, MouseEvent>
      | React.KeyboardEvent<HTMLDivElement | HTMLAnchorElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    router.back();
  };

  const onAddProductsClickHander = () => {
    setShowDrawer(true);
  };

  const { updateCustomList }: CustomListContextType = useContext(AllCustomListContext);

  const deleteInvalidProduct = () => {
    const invalidProductEntrtIds = invalidProducts.map((product) => product.entryId);
    myListApi
      .removeEntryFromList({
        listId: listDetails?.listId,
        entryId: invalidProductEntrtIds as number[],
      })
      .then((res) => {
        updateCustomList(res);
        setShowModal(false);
      });
  };

  const showInvalidProductModal = () => {
    const restrictedSharingProduct = listDetails?.payLoad?.entries?.filter(
      (entry) => entry.product.flags?.restrictFromSharing,
    );

    if (restrictedSharingProduct?.length) {
      setInvalidProducts(restrictedSharingProduct);
      setShowModal(true);
    }

    return Boolean(restrictedSharingProduct?.length);
  };
  const categoryTextColor = getCategoryColor(
    categoryData as categoryDataType[],
    listDetails?.payLoad?.category as string,
    false,
    false,
  );
  const buttonThemeColor = getButtonThemeColor(categoryData, listDetails?.payLoad?.category as string);
  return (
    <>
      {showDrawer && <AddProductsDrawer onDrawerClose={() => setShowDrawer(false)} />}
      <div
        className={`${
          listDetails?.listType === ListType.Wishlist ? styles['myList-banner-wishlist'] : styles['myList-banner']
        }`}
        style={
          listDetails?.listType === ListType.Wishlist
            ? {}
            : {
                backgroundColor: getCategoryColor(
                  categoryData as categoryDataType[],
                  listDetails?.payLoad?.category as string,
                  false,
                  true,
                ),
              }
        }
      >
        {listDetails?.listType === ListType.Wishlist && (
          <>
            <span className={styles['myList-banner-wishlist-header-back']}>
              <TextLink
                href=""
                text={t('cart.myListsPage.back')}
                onClick={onChevronLeftClick}
                textLinkStyle={TextLinkStyle.CHEVRON}
              />
            </span>

            <div className={styles['myList-banner-heading']}>
              <h3 className={styles['myList-banner-heading-container']}>
                <span className={styles['myList-banner-heading-container-left-icon']}>
                  <FAIcon iconName="chevron-left" onClick={onChevronLeftClick} />
                </span>
                {t('cart.listDetails.Wishlist')}
              </h3>
              <div className={styles['myList-banner-heading-right-icons']}>
                <GenericLink href={CartRoutePaths.CART}>
                  <MiniCart elementClass={styles['myLists-icon']} />
                </GenericLink>
              </div>
            </div>
          </>
        )}
        {listDetails?.listType !== ListType.Wishlist && (
          <>
            <div className={styles['myList-banner-description']}>
              <div className={styles['myList-banner-description-icon-container']}>
                {!listDetails?.isReceiver && (
                  <div
                    aria-label="banner-description-icon-container-backIcon"
                    tabIndex={0}
                    role="button"
                    onKeyDown={onChevronLeftClick}
                    onClick={onChevronLeftClick}
                    className={styles['myList-banner-description-icon-container-backIcon']}
                  >
                    <FAIcon iconName="chevron-left" />
                  </div>
                )}
                <div className={styles['myList-banner-description-icon-container-minicart']}>
                  <div
                    aria-label="banner-description-icon-container-cartIcon"
                    tabIndex={0}
                    role="button"
                    className={styles['myList-banner-description-icon-container-minicart-cartIcon']}
                  >
                    <GenericLink href={CartRoutePaths.CART}>
                      <MiniCart />
                    </GenericLink>
                  </div>
                </div>
              </div>

              <span
                className={styles['myList-banner-description-heading']}
                style={{
                  color: categoryTextColor,
                }}
              >
                {capitalizeFirstLetter(listDetails?.payLoad?.name || '')}
              </span>
              {!listDetails?.isReceiver && listDetails?.payLoad?.category && (
                <span className={styles['myList-banner-description-tag-container']}>
                  <DisplayListType
                    isPrivate={listDetails.listType === ListType.Private}
                    isDisplayOnMyShop={listDetails.isDisplayedOnShop || false}
                    listCategory={categoryName}
                    iconTheme={buttonThemeColor}
                    className={styles['list-type-container']}
                    categoryTextColor={categoryTextColor}
                  />
                </span>
              )}
              <span
                style={{
                  color: categoryTextColor,
                }}
              >
                {listDetails?.payLoad?.description}
              </span>
            </div>

            {!listDetails?.isReceiver && (
              <div className={styles['myList-banner-button-wrapper']}>
                <div className={styles['myList-banner-button']}>
                  <Button
                    id="add Products"
                    title={t('cart.listBanner.addProducts')}
                    tabIndex={0}
                    theme={buttonThemeColor}
                    buttonStyle={isABO() ? ButtonStyles.OUTLINE : ButtonStyles.SOLID}
                    onClick={onAddProductsClickHander}
                    data-testid="add-products-test-id"
                    aria-label="add-products"
                  />
                  {isABO() && (
                    <>
                      {showModal ? (
                        <ModalWrapper
                          headLineText={headLineText}
                          description={description}
                          primaryButtonLabel={primaryLabelText}
                          secondaryButtonLabel={secondaryLabelText}
                          onPrimarySuccess={() => setShowModal(false)}
                          onSecondarySuccess={deleteInvalidProduct}
                          productData={invalidProducts}
                        />
                      ) : (
                        <div
                          aria-label="share-btn-container"
                          role="button"
                          tabIndex={0}
                          onKeyDown={showInvalidProductModal}
                          onClick={showInvalidProductModal}
                          className={styles['share-btn']}
                        >
                          <SocialShareLink
                            linkProps={{
                              category: 'myList',
                              data: '',
                              fullUrl: window?.location?.href,
                              title: listDetails?.payLoad?.name,
                            }}
                            buttonProps={{
                              title: t('cart.listBanner.shareList'),
                              disabled: listDetails?.listType === ListType.Private,
                              theme: buttonThemeColor,
                            }}
                            isCustomUrl={false}
                            shareUrl={false}
                            customClass={styles['sharebar-modal']}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
                {listDetails && (
                  <ListMenu
                    showLoaderCB={showLoaderCB}
                    key="list-menu"
                    listDetails={listDetails}
                    onListUpdate={(list?: ListResponse) => {
                      list && updateCustomList(list);
                    }}
                    showInListDetails={true}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
